import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

export const TICKET_STATUSES = [
  { value: "", label: "All statuses" },
  { value: "OPEN", label: "Open" },
  { value: "IN_PROGRESS", label: "In progress" },
  { value: "WAITING", label: "Waiting" },
  { value: "RESOLVED", label: "Resolved" },
  { value: "CLOSED", label: "Closed" },
];

export const TICKET_CATEGORIES = [
  { value: "GENERAL", label: "General" },
  { value: "FEES", label: "Fees" },
  { value: "HOMEWORK", label: "Homework" },
  { value: "ATTENDANCE", label: "Attendance" },
  { value: "OTHER", label: "Other" },
];

export const TICKET_PRIORITIES = [
  { value: "LOW", label: "Low" },
  { value: "NORMAL", label: "Normal" },
  { value: "HIGH", label: "High" },
  { value: "URGENT", label: "Urgent" },
];

export const PARTICIPANT_ROLES = [
  { value: "TEACHER", label: "Teacher" },
  { value: "PARENT", label: "Parent" },
];

export const QUICK_REPLIES = [
  {
    label: "Acknowledged",
    body: "Thank you for contacting the school office. Your message has been received and is under review. We will respond once we have an update.",
  },
  {
    label: "Request details",
    body: "Thank you for your message. Please provide the student's full name, class, and the date of the matter so we can review the records.",
  },
  {
    label: "Attendance",
    body: "Thank you for bringing this to our attention. The attendance record will be reviewed, and any correction required will be made.",
  },
  {
    label: "Fees",
    body: "Thank you for your enquiry regarding fees. The account will be reviewed, and the relevant details will be shared with you shortly.",
  },
  {
    label: "Homework",
    body: "Thank you for your enquiry. The matter will be confirmed with the class teacher, and we will reply with the details.",
  },
  {
    label: "Resolved",
    body: "This matter has been resolved. Please reply to this message if any further assistance is required.",
  },
];

export const HELP_ARTICLES = [
  {
    title: "Fees and receipts",
    body: "Parents can ask for a missing receipt, a fee reminder, or a payment question. Include the student name and the month in the ticket.",
  },
  {
    title: "Attendance",
    body: "Use a ticket when a mark looks wrong or a leave was not recorded. Add the date and the student so the office can check the register.",
  },
  {
    title: "Homework and classwork",
    body: "Teachers and parents can ask about a missing assignment or a due date. Name the class and the day the work was set.",
  },
];

export function assigneeLabel(ticket, userId) {
  const assigned = ticket?.assignedAdmin;
  const id = assigned?._id || assigned;
  if (!id) return "Unassigned";
  const name = `${assigned?.firstName || ""} ${assigned?.lastName || ""}`.trim();
  if (name) return name;
  if (userId && String(id) === String(userId)) return "You";
  return "Assigned";
}

export function ticketStatusLabel(status) {
  return TICKET_STATUSES.find((item) => item.value === status)?.label || status || "—";
}

export function ticketCategoryLabel(category) {
  return TICKET_CATEGORIES.find((item) => item.value === category)?.label || category || "—";
}

export function ticketPriorityLabel(priority) {
  return TICKET_PRIORITIES.find((item) => item.value === priority)?.label || priority || "—";
}

export function participantRoleLabel(role) {
  return PARTICIPANT_ROLES.find((item) => item.value === role)?.label || role || "—";
}

export function ticketTime(value) {
  if (!value) return "—";
  return dayjs(value).fromNow();
}

export function ticketDayLabel(value) {
  if (!value) return "";
  const date = dayjs(value);
  if (date.isSame(dayjs(), "day")) return "Today";
  if (date.isSame(dayjs().subtract(1, "day"), "day")) return "Yesterday";
  return date.format("D MMM YYYY");
}

export function ticketClock(value) {
  if (!value) return "";
  return dayjs(value).format("hh:mm a");
}

export function ticketDate(value) {
  if (!value) return "—";
  return dayjs(value).format("MMM D, YYYY h:mm A");
}

export function ticketPreview(text = "", max = 90) {
  const value = String(text || "").trim();
  if (!value) return "No messages yet";
  return value.length > max ? `${value.slice(0, max).trim()}…` : value;
}

export function childLabel(child) {
  if (!child) return "";
  return `${child.firstName || ""} ${child.lastName || ""}`.trim();
}

export function teacherOptionLabel(teacher) {
  return `${teacher?.firstName || ""} ${teacher?.lastName || ""}`.trim() || "Teacher";
}

export function parentOptionLabel(parent) {
  const father = `${parent?.fatherFirstName || ""} ${parent?.fatherLastName || ""}`.trim();
  if (father) return father;
  return `${parent?.motherFirstName || ""} ${parent?.motherLastName || ""}`.trim() || "Parent";
}

export function statusTone(status) {
  switch (status) {
    case "OPEN":
      return "is-open";
    case "IN_PROGRESS":
      return "is-progress";
    case "WAITING":
      return "is-waiting";
    case "RESOLVED":
      return "is-resolved";
    case "CLOSED":
      return "is-closed";
    default:
      return "is-default";
  }
}

export function priorityTone(priority) {
  switch (priority) {
    case "URGENT":
      return "is-urgent";
    case "HIGH":
      return "is-high";
    case "LOW":
      return "is-low";
    default:
      return "is-normal";
  }
}

export function hasAdminUnread(ticket) {
  return Number(ticket?.adminUnreadCount || 0) > 0;
}

export function contactInitials(name = "") {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
}

export function resolveParticipantImage(ticket, { teachers = [], parents = [] } = {}) {
  if (ticket?.participantImage) return ticket.participantImage;

  const participantId = String(ticket?.participant?._id || ticket?.participant || "");
  if (!participantId) return "";

  if (ticket?.participantRole === "TEACHER") {
    return teachers.find((item) => String(item._id) === participantId)?.image || "";
  }

  if (ticket?.participantRole === "PARENT") {
    return parents.find((item) => String(item._id) === participantId)?.image || "";
  }

  return "";
}
